import React, { useState, useEffect } from 'react';
import { Bookmark, Check } from 'lucide-react';
import { saveQuestionForUser, removeSavedQuestion, checkIfQuestionSaved } from '../firebase/collections';
import { useRealtime } from '../contexts/RealtimeContext';

const BookmarkButton = ({ question, testInfo, className = "", onBookmarkChange }) => {
  const { currentUser } = useRealtime();
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savedDocId, setSavedDocId] = useState(null);

  // Check if question is already saved when component mounts
  useEffect(() => {
    if (currentUser?.uid && question?.id) {
      checkSavedStatus();
    }
  }, [currentUser?.uid, question?.id]);

  const checkSavedStatus = async () => {
    try {
      const result = await checkIfQuestionSaved(currentUser.uid, question.id);
      if (result.success) {
        setIsSaved(result.isSaved);
        setSavedDocId(result.docId);
      }
    } catch (error) {
      // Error checking saved status
    }
  };

  const handleToggleBookmark = async () => {
    if (!currentUser?.uid) {
      return;
    }

    // BookmarkButton: Toggling bookmark for question
    setIsLoading(true);
    try {
      if (isSaved) {
        // BookmarkButton: Removing question from saved list
        // Remove from saved questions
        const result = await removeSavedQuestion(savedDocId);
        // BookmarkButton: Remove result
        if (result.success) {
          setIsSaved(false);
          setSavedDocId(null);
          if (onBookmarkChange) {
            onBookmarkChange(false);
          }
        } else {
        }
      } else {
        // BookmarkButton: Saving question
        // Save question
        const questionData = {
          questionId: question.id,
          question: question.question,
          type: question.type || 'multiple-choice',
          options: question.options || [],
          correctAnswer: question.correctAnswer,
          explanation: question.explanation || '',
          topic: question.topic || question.subject || 'General',
          subject: question.subject || question.topic || 'General',
          difficulty: question.difficulty || 'medium',
          testId: testInfo?.id || 'unknown',
          testName: testInfo?.name || 'Unknown Test',
          markingScheme: question.markingScheme || { correct: 4, incorrect: -1, unattempted: 0 }
        };

        // BookmarkButton: Question data to save
        const result = await saveQuestionForUser(currentUser.uid, questionData);
        // BookmarkButton: Save result
        if (result.success) {
          setIsSaved(true);
          setSavedDocId(result.id);
          if (onBookmarkChange) {
            onBookmarkChange(true);
          }
        } else {
        }
      }
    } catch (error) {
      // BookmarkButton: Error toggling bookmark
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleToggleBookmark}
      disabled={isLoading}
      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
        isSaved
          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
      } ${className}`}
      title={isSaved ? 'Remove from saved questions' : 'Save question for later'}
    >
      {isLoading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
      ) : isSaved ? (
        <Check size={16} />
      ) : (
        <Bookmark size={16} />
      )}
      <span className="text-sm font-medium">
        {isSaved ? 'Saved' : 'Save'}
      </span>
    </button>
  );
};

export default BookmarkButton;
